import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import TeamChat from '../components/chat/TeamChat';
import api from '../services/api';
import { toast } from 'sonner';
import {
  MessageSquare, Search, RefreshCw, Loader2, Users, Trophy, Archive, Inbox,
} from 'lucide-react';
import { formatDateOnly } from '../utils/timezone';

function relativeTime(iso) {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.round(diff / 60000);
    if (m < 1) return 'now';
    if (m < 60) return `${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.round(h / 24);
    if (d < 7) return `${d}d`;
    return formatDateOnly(iso);
  } catch {
    return '';
  }
}

export default function TeamChats() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // {team_id, team_name}
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('active');         // active | archived

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/chat/teams');
      setRooms(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load team chats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh every 20s while on the index view (polling fallback for unread counts)
  useEffect(() => {
    if (selected) return;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [selected, load]);

  const filtered = rooms.filter((r) => {
    if (tab === 'active' && r.is_archived) return false;
    if (tab === 'archived' && !r.is_archived) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.team_name?.toLowerCase().includes(q) ||
      r.tournament_name?.toLowerCase().includes(q) ||
      r.age_category?.toLowerCase().includes(q)
    );
  });

  const totalUnread = rooms
    .filter((r) => !r.is_archived)
    .reduce((s, r) => s + (r.unread_count || 0), 0);

  // Detail view
  if (selected) {
    return (
      <DashboardLayout>
        <div className="h-[calc(100vh-8rem)]" data-testid="team-chat-detail">
          <TeamChat
            teamId={selected.team_id}
            teamName={selected.team_name}
            onBack={() => {
              setSelected(null);
              load();           // refresh unread counts on return
            }}
          />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 max-w-4xl" data-testid="team-chats-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-blue-600" />
              Team Chats
              {totalUnread > 0 && (
                <Badge variant="destructive" className="ml-1" data-testid="total-unread-badge">
                  {totalUnread}
                </Badge>
              )}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Real-time chat groups for every team your child is on.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="active" data-testid="tab-active">
              <Inbox className="w-3.5 h-3.5 mr-1" />
              Active ({rooms.filter(r => !r.is_archived).length})
            </TabsTrigger>
            <TabsTrigger value="archived" data-testid="tab-archived">
              <Archive className="w-3.5 h-3.5 mr-1" />
              Archived ({rooms.filter(r => r.is_archived).length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-3 text-muted-foreground" />
          <Input
            placeholder="Search by team, tournament, or age..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            data-testid="team-search-input"
          />
        </div>

        {/* Rooms */}
        <Card>
          <CardHeader className="p-3 lg:p-4 border-b">
            <CardTitle className="text-sm">
              {filtered.length} {filtered.length === 1 ? 'team' : 'teams'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading && (
              <div className="p-6 text-center text-muted-foreground text-sm">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                Loading team chats...
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="p-8 text-center text-muted-foreground text-sm" data-testid="empty-teams">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
                {search ? (
                  <div>No teams match "<strong>{search}</strong>".</div>
                ) : tab === 'archived' ? (
                  <div>No archived team chats.</div>
                ) : (
                  <div>
                    No team chats yet.<br />
                    <span className="text-xs">Teams appear here once your child is added to a tournament team roster.</span>
                  </div>
                )}
              </div>
            )}
            {!loading && filtered.map((r) => (
              <button
                key={r.team_id}
                onClick={() => setSelected({ team_id: r.team_id, team_name: r.team_name })}
                className="w-full text-left p-3 border-b hover:bg-accent transition-colors flex items-start gap-3"
                data-testid={`team-row-${r.team_id}`}
              >
                {/* Avatar */}
                <div className="shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                  {(r.team_name || 'T').slice(0, 2).toUpperCase()}
                </div>
                {/* Content */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-sm truncate">{r.team_name}</span>
                        {r.age_category && (
                          <Badge variant="outline" className="text-[10px] py-0 h-4">{r.age_category}</Badge>
                        )}
                        {r.is_archived && (
                          <Badge variant="outline" className="text-[10px] py-0 h-4 border-amber-400 text-amber-700">Archived</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate mt-0.5 flex items-center gap-1">
                        <Trophy className="w-3 h-3 shrink-0" />
                        {r.tournament_name}
                        <span className="mx-1">·</span>
                        <Users className="w-3 h-3 shrink-0" />
                        {r.participant_count}
                      </div>
                      <div className="text-xs text-muted-foreground truncate mt-1">
                        {r.last_message ? (
                          <>
                            <span className="font-medium">{r.last_message.sender_name}:</span>{' '}
                            {r.last_message.message}
                          </>
                        ) : (
                          <span className="italic">No messages yet — say hi!</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {r.last_message?.created_at && (
                        <span className="text-[10px] text-muted-foreground">
                          {relativeTime(r.last_message.created_at)}
                        </span>
                      )}
                      {(r.unread_count || 0) > 0 && (
                        <Badge variant="destructive" className="text-[10px] h-5 px-1.5" data-testid={`unread-badge-${r.team_id}`}>
                          {r.unread_count}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
